import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  SalesOrderReportParams,
  SalesOrderReportRecord,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

const SalesOrderReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<SalesOrderReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // State for filters
  const [filters, setFilters] = useState<SalesOrderReportParams>({
    product_id: undefined,
    product_name: "",
    customer_name: "",
    location_id: undefined,
    sales_order_id: undefined,
    status: "",
    job_id: undefined,
    start_date: getDefaultDates().startDate,
    end_date: getDefaultDates().endDate,
    page: 1,
    page_size: 50,
  });

  // State for dropdowns
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);

  // Selected values
  const [selectedWarehouse, setSelectedWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);

  // Pagination for dropdowns
  const [warehousesCurrentPage, setWarehousesCurrentPage] = useState(0);
  const [warehousesSearchQuery, setWarehousesSearchQuery] = useState("");
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const rowsPerPage = 10;

  // Order status options
  const orderStatusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "CLOSED", label: "Closed" },
  ];

  // Fetch functions for dropdowns
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehousesCurrentPage,
        rowsPerPage,
        warehousesSearchQuery
      );
      setWarehouses((prev) =>
        warehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehousesCurrentPage, warehousesSearchQuery]);

  // Handle scroll for infinite loading
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 1) {
      if (hasMoreWarehouses) {
        setWarehousesCurrentPage((prev) => prev + 1);
      }
    }
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: SalesOrderReportParams = {
        ...filters,
        location_id: selectedWarehouse?.id || filters.location_id,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove empty string values
      Object.keys(params).forEach((key) => {
        const k = key as keyof SalesOrderReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      const response = await AllReportsApi.api.getSalesOrderReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch sales order report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize, selectedWarehouse]);

  // Initial fetch for dropdowns
  useEffect(() => {
    const debouncedFetchWarehouses = debounce(fetchWarehouses, 500);
    debouncedFetchWarehouses();
    return () => debouncedFetchWarehouses.cancel();
  }, [fetchWarehouses, warehousesCurrentPage]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange =
    (field: keyof SalesOrderReportParams) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1);
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      product_id: undefined,
      product_name: "",
      customer_name: "",
      location_id: undefined,
      sales_order_id: undefined,
      status: "",
      job_id: undefined,
      start_date: defaultDates.startDate,
      end_date: defaultDates.endDate,
    });
    setSelectedWarehouse(null);
    setCurrentPage(1);
  };
  const handleNumericInputChange =
    (field: "product_id" | "sales_order_id") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Allow only numbers and empty string
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };
  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const params: SalesOrderReportParams = {
        ...filters,
        location_id: selectedWarehouse?.id || filters.location_id,
      };

      // Remove pagination params and empty values
      delete params.page;
      delete params.page_size;

      Object.keys(params).forEach((key) => {
        const k = key as keyof SalesOrderReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      await AllReportsApi.api.downloadSalesOrderReport(params);
    } catch (err) {
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Get status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      case "PARTIALLY_COMPLETED":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Sales Order Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              Track all sales orders with detailed product and customer
              information
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              <Autocomplete
                fullWidth
                size="small"
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={selectedWarehouse}
                onChange={(event, newValue) => {
                  setSelectedWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setWarehousesCurrentPage(0);
                  setWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleScroll,
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Reference Code"
                value={filters.product_id || ""}
                onChange={handleFilterChange("product_id")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Product Name"
                value={filters.product_name}
                onChange={handleFilterChange("product_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Customer Name"
                value={filters.customer_name}
                onChange={handleFilterChange("customer_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Sales Order ID"
                value={filters.sales_order_id || ""}
                onChange={handleNumericInputChange("sales_order_id")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              {/* <TextField
                label="Job ID"
                type="number"
                value={filters.job_id || ""}
                onChange={handleFilterChange("job_id")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              /> */}
              <TextField
                select
                label="Order Status"
                value={filters.status}
                onChange={handleFilterChange("status")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}>
                <MenuItem value="">
                  <em>All Statuses</em>
                </MenuItem>
                {orderStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange("start_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange("end_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>SO ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fleet Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Job ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Job Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Warehouse
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Quantity
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Cost Price
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Sales Price
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Created Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Last Updated
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Updated By
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row, index) => (
                          <TableRow
                            key={`${row.sales_order_id}-${index}`}
                            sx={{
                              "&:hover": { bgcolor: "#fafafa" },
                              "&:last-child td": { border: 0 },
                            }}>
                            <TableCell>
                              <Chip
                                label={row.sales_order_id}
                                size="small"
                                sx={{
                                  bgcolor: "#000",
                                  color: "#fff",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {row.product_name}
                                </Typography>
                                {/* <Typography
                                  variant="caption"
                                  color="text.secondary">
                                  ID: {row.product_name}
                                </Typography> */}
                              </Box>
                            </TableCell>
                            <TableCell>{row.reference_code || "N/A"}</TableCell>
                            <TableCell>{row.type || "N/A"}</TableCell>
                            <TableCell>{row.fleet_type || "N/A"}</TableCell>
                            <TableCell>{row.job_id || "N/A"}</TableCell>
                            <TableCell>{row.job_type || "N/A"}</TableCell>
                            <TableCell>{row.customer_name || "N/A"}</TableCell>
                            <TableCell>{row.hub_name}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.order_status}
                                size="small"
                                color={getStatusColor(row.order_status)}
                              />
                            </TableCell>
                            <TableCell align="right">{row.quantity}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(row.cost_price)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(row.sales_price)}
                            </TableCell>
                            <TableCell>{row.created_date}</TableCell>
                            <TableCell>
                              {row.updated_time ? (
                                <Tooltip
                                  title={`Updated by: ${
                                    row.updated_by || "System"
                                  }`}>
                                  <span>{row.updated_time}</span>
                                </Tooltip>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>{row.updated_by}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e as any)}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SalesOrderReport;
